import React from 'react';
import { useI18n } from '../../../../i18n';
import { useAuth } from '../../../../context/AuthContext';
import { LoadingSpinner } from '../../../shared/components/LoadingSpinner';
import { useAdminIA } from '../hooks/useAdminIA';
import { AdminIAEvalsTab } from './AdminIAEvalsTab';
import { AdminIADashboard } from '../components/AdminIADashboard';
import { AdminIAProviders } from '../components/AdminIAProviders';
import { AdminIAChatConfig } from '../components/AdminIAChatConfig';
import { AdminIAAssistModes } from '../components/AdminIAAssistModes';
import { AdminIAAdvanced } from '../components/AdminIAAdvanced';

const PILL_ACTIVE: React.CSSProperties = {
  padding: '8px 16px',
  borderRadius: 8,
  border: 'none',
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
  backgroundColor: '#1F3864',
  color: '#fff',
  fontFamily: 'inherit',
  textAlign: 'left',
};

const PILL_INACTIVE: React.CSSProperties = {
  padding: '8px 16px',
  borderRadius: 8,
  border: '1px solid #e5e7eb',
  fontSize: 13,
  fontWeight: 500,
  cursor: 'pointer',
  backgroundColor: '#fff',
  color: '#6b7280',
  fontFamily: 'inherit',
  textAlign: 'left',
};

export function AdminIATab() {
  const { t } = useI18n();
  const h = useAdminIA();
  // 0581: gate canEdit por rol (defensa en profundidad). El filtro de tabs ya
  // excluye TENANT_AUDITOR/MEMBER pero el componente no debe asumirlo.
  const { user } = useAuth();
  const role = user?.role ?? 'MEMBER';
  const canEdit =
    role === 'SUPERADMIN_PLATFORM' || role === 'TENANT_OWNER' || role === 'TENANT_ADMIN';

  const pills: { key: typeof h.activePill; label: string }[] = [
    { key: 'status', label: t.admin.ia.pills.status },
    { key: 'config', label: t.admin.ia.pills.config },
    { key: 'evals', label: t.admin.ia.pills.evals },
  ];

  if (h.loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 48 }}>
        <LoadingSpinner text={t.admin.via.cargando} />
      </div>
    );
  }

  if (h.error) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          padding: 48,
          gap: 12,
        }}
      >
        <div style={{ color: '#DC2626', fontSize: 14 }}>{h.error}</div>
        <button
          onClick={h.loadData}
          style={{
            padding: '6px 14px',
            background: '#0E243E',
            color: '#fff',
            border: 'none',
            borderRadius: 6,
            fontSize: 12,
            fontWeight: 600,
            cursor: 'pointer',
            fontFamily: 'inherit',
          }}
        >
          Reintentar
        </button>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {!canEdit && (
        <div
          style={{
            padding: '10px 16px',
            borderRadius: 8,
            fontSize: 13,
            backgroundColor: '#EFF6FF',
            color: '#1E40AF',
            border: '1px solid #BFDBFE',
          }}
        >
          {t.admin.usuarios.iaReadOnly}
        </div>
      )}
      <div style={{ display: 'flex', gap: 24 }}>
        <div
          style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 170, flexShrink: 0 }}
        >
          {pills.map((p) => (
            <button
              key={p.key}
              onClick={() => h.setActivePill(p.key)}
              style={h.activePill === p.key ? PILL_ACTIVE : PILL_INACTIVE}
            >
              {p.label}
            </button>
          ))}
        </div>

        <fieldset
          disabled={!canEdit}
          style={{
            flex: 1,
            border: 'none',
            padding: 0,
            margin: 0,
            opacity: canEdit ? 1 : 0.6,
          }}
        >
          {h.activePill === 'status' && (
            <AdminIADashboard
              dashData={h.dashData}
              dashLoading={h.dashLoading}
              dashFiltering={h.dashFiltering}
              dashPeriodo={h.dashPeriodo}
              onPeriodoChange={h.handlePeriodoChange}
            />
          )}

          {h.activePill === 'config' && (
            <div style={{ maxWidth: 800, overflowY: 'auto' }}>
              {h.feedbackMsg && (
                <div
                  style={{
                    position: 'fixed',
                    top: 16,
                    right: 16,
                    zIndex: 1000,
                    color: '#15803D',
                    fontSize: 13,
                    fontWeight: 600,
                    fontFamily: 'Montserrat, sans-serif',
                    background: '#DCFCE7',
                    padding: '8px 16px',
                    borderRadius: 8,
                    animation: 'fadeIn 0.2s ease',
                  }}
                >
                  {h.feedbackMsg}
                </div>
              )}

              <AdminIAProviders
                providers={h.providers}
                showNewProvider={h.showNewProvider}
                newProvider={h.newProvider}
                onSetShowNewProvider={h.setShowNewProvider}
                onSetNewProvider={h.setNewProvider}
                onUpdateProviderLocal={h.updateProviderLocal}
                onSaveProviderKey={h.handleSaveProviderKey}
                onEditKey={h.handleEditKey}
                onCancelEditKey={h.handleCancelEditKey}
                onToggleProviderActive={h.handleToggleProviderActive}
                onTestKey={h.handleTestKey}
                onCreateProvider={h.handleCreateProvider}
                providerTypeLabel={h.providerTypeLabel}
              />

              <AdminIAChatConfig
                chatModel={h.chatModel}
                chatLimit={h.chatLimit}
                chatProviderOptions={h.chatProviderOptions}
                fallbackChain={h.fallbackChain}
                providers={h.providers}
                configData={h.configData}
                ragStatus={h.ragStatus}
                reindexing={h.reindexing}
                reindexForce={h.reindexForce}
                onReindexForceChange={h.setReindexForce}
                reindexIntervalMs={h.reindexIntervalMs}
                onReindexIntervalChange={h.handleReindexIntervalChange}
                reindexHistory={h.reindexHistory}
                onChatModelChange={h.handleChatModelChange}
                onChatLimitChange={h.handleChatLimitChange}
                onRemoveFallback={h.removeFallback}
                onAddFallback={h.addFallback}
                onReorderFallback={h.reorderFallback}
                onReindex={h.handleReindex}
              />

              <AdminIAAssistModes
                assistModes={h.assistModes}
                modelOptions={h.assistModelOptions}
                saving={h.saving}
                onUpdateAssistMode={h.updateAssistMode}
              />

              <AdminIAAdvanced
                comportamiento={h.comportamiento}
                contexto={h.contexto}
                glossary={h.glossary}
                configData={h.configData}
                saving={h.saving}
                onSetComportamiento={h.setComportamiento}
                onSetContexto={h.setContexto}
                onSetGlossary={h.setGlossary}
                onUpdateGlossaryTerm={h.updateGlossaryTerm}
                onRemoveGlossaryTerm={h.removeGlossaryTerm}
                onSaveConfig={h.saveConfig}
                onSetConfigData={h.setConfigData}
              />
            </div>
          )}

          {h.activePill === 'evals' && <AdminIAEvalsTab />}
        </fieldset>
      </div>
    </div>
  );
}
